import { Injectable } from '@angular/core';
import { environment } from '../../../../my-app/src/environments/environment';

export type WrapperStreamEventName = 'accepted' | 'meta' | 'chunk' | 'done' | 'error' | string;

export interface WrapperStreamEvent<T = any> {
  event: WrapperStreamEventName;
  data: T;
  rawData: string;
}

@Injectable({
  providedIn: 'root'
})
export class WrapperStreamService {
  private readonly wrapperApiBaseUrl: string;

  constructor() {
    // Reuse the existing environment.agentApiUrl. In dev this is already set to the Java wrapper API base.
    // Expected example: http://localhost:18080/wrapper/api
    this.wrapperApiBaseUrl = (environment.agentApiUrl || '')
      .trim()
      .replace(/\/+$/, '')
      .replace(/\/stream$/i, '');
  }

  async stream(
    filePath: string,
    query: string,
    handlers: {
      onEvent?: (event: WrapperStreamEvent) => void;
      onMeta?: (data: any) => void;
      onChunk?: (data: any) => void;
      onDone?: (data: any) => void;
      onError?: (data: any) => void;
    },
    signal?: AbortSignal
  ): Promise<any> {
    const url = `${this.wrapperApiBaseUrl}/stream`;
    const body = JSON.stringify({ filePath, query });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      },
      body,
      signal,
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`Wrapper stream HTTP ${response.status}: ${text}`);
    }

    if (!response.body) {
      throw new Error('Wrapper stream response has no body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let donePayload: any = null;

    const dispatchSseBlock = (block: string): void => {
      const parsed = this.parseSseBlock(block);
      if (!parsed) {
        return;
      }

      handlers.onEvent?.(parsed);

      if (parsed.event === 'meta') {
        handlers.onMeta?.(parsed.data);
      } else if (parsed.event === 'chunk') {
        handlers.onChunk?.(parsed.data);
      } else if (parsed.event === 'done') {
        donePayload = parsed.data;
        handlers.onDone?.(parsed.data);
      } else if (parsed.event === 'error') {
        handlers.onError?.(parsed.data);
      }
    };

    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      buffer = buffer.replace(/\r\n/g, '\n');

      const parts = buffer.split('\n\n');
      buffer = parts.pop() ?? '';

      for (const part of parts) {
        dispatchSseBlock(part);
      }
    }

    buffer += decoder.decode();
    buffer = buffer.replace(/\r\n/g, '\n');
    for (const part of buffer.split('\n\n')) {
      dispatchSseBlock(part);
    }

    return donePayload;
  }

  private parseSseBlock(block: string): WrapperStreamEvent | null {
    const trimmed = block.trim();
    if (!trimmed) {
      return null;
    }

    let eventName: string = 'message';
    const dataLines: string[] = [];

    for (const line of trimmed.split('\n')) {
      if (line.startsWith('event:')) {
        eventName = line.slice('event:'.length).trim();
        continue;
      }
      if (line.startsWith('data:')) {
        dataLines.push(line.slice('data:'.length).trimStart());
        continue;
      }
    }

    const rawData = dataLines.join('\n').trim();
    let data: any = rawData;
    if (rawData) {
      try {
        data = JSON.parse(rawData);
      } catch {
        // keep as plain string
      }
    }

    return {
      event: eventName,
      data,
      rawData,
    };
  }
}

